import React, { useState, useMemo } from 'react';
import { 
  ShoppingBag, 
  Search, 
  Plus, 
  Tag, 
  Edit3, 
  Trash2, 
  TrendingUp, 
  DollarSign, 
  Package, 
  Boxes, 
  Check, 
  X, 
  AlertCircle, 
  Percent, 
  ArrowUpDown, 
  Camera, 
  Image as ImageIcon, 
  Loader2, 
  Zap,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  MapPin,
  Layers,
  Barcode,
  Hash,
  Truck,
  FileText,
  Archive,
  RefreshCw
} from 'lucide-react';
import { UserProfile, Material, FinancialConfig, StockControlMode } from '../types';
import { formatCurrencyBRL } from '../utils/formatUtils';
import { compressProductImage, uploadProductPhoto, CompressedImageResult } from '../utils/imageCompressionUtils';
import { motion, AnimatePresence } from 'motion/react';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';
import { useHardwareScanner } from '../modules/erp/ind/hooks/useHardwareScanner';

interface ProductCatalogManagerProps {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  onSaveMaterials: (materials: Material[]) => void;
  onBack: () => void;
  onNavigateToStock?: () => void;
}

// Sugestões de NCM frequentes por nicho para facilitar a vida do empreendedor
const NCM_SUGGESTIONS: Array<{ ncm: string; label: string; niche: string }> = [
  { ncm: '3305.90.00', label: 'Cosméticos Capilares (Pomadas, Óleos, Shampoos)', niche: 'Beleza & Barbearia' },
  { ncm: '3307.10.00', label: 'Preparações para Barbear e Loções Pós-Barba', niche: 'Beleza & Barbearia' },
  { ncm: '8536.20.00', label: 'Disjuntores Elétricos de Proteção', niche: 'Instalações & Elétrica' },
  { ncm: '8544.49.00', label: 'Condutores, Cabos e Fios Elétricos Isolados', niche: 'Instalações & Elétrica' },
  { ncm: '8708.29.99', label: 'Partes e Acessórios de Veículos / Autopeças', niche: 'Automotivo & Mecânica' },
  { ncm: '1905.90.90', label: 'Bolos, Pães, Tortas e Confeitaria', niche: 'Alimentação & Confeitaria' },
  { ncm: '6109.10.00', label: 'Camisetas e Vestuário de Malha de Algodão', niche: 'Moda & Confecção' },
  { ncm: '3926.90.90', label: 'Artigos Plásticos Diversos e Acessórios', niche: 'Varejo & Geral' }
];

const COMMON_UNITS = ['un', 'cx', 'pct', 'kg', 'm', 'kit', 'lata', 'rolo', 'par'];

export default function ProductCatalogManager({
  user,
  config,
  materials,
  onSaveMaterials,
  onBack,
  onNavigateToStock
}: ProductCatalogManagerProps) {
  const { isMaster, can } = useTeamPermissions();
  const canAccessStock = isMaster || can('view_cost_center');

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Material | null>(null);

  // 1. Estados Básicos (Modo Express - Rápido & Funcional)
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [costPrice, setCostPrice] = useState<number>(0);
  const [marginPercent, setMarginPercent] = useState<number>(40);
  const [salePrice, setSalePrice] = useState<number>(0);
  const [unit, setUnit] = useState('un');
  const [stockQuantity, setStockQuantity] = useState<number>(1);
  const [barcode, setBarcode] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [stockControlMode, setStockControlMode] = useState<StockControlMode>('unit_sale');
  const [packQuantity, setPackQuantity] = useState<number>(1);
  const [scannerBanner, setScannerBanner] = useState<string | null>(null);

  // Estados para Modal de Confirmação de Exclusão e Desativação (Material Design 3)
  const [productToDelete, setProductToDelete] = useState<Material | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  
  // 2. Estados da Tabela Master (Revelação Progressiva / Opcional)
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const [activeAdvancedTab, setActiveAdvancedTab] = useState<'master' | 'stock' | 'niche'>('master');
  const [sku, setSku] = useState('');
  const [ncm, setNcm] = useState('');
  const [cest, setCest] = useState('');
  const [originCode, setOriginCode] = useState<number>(0);
  const [minStock, setMinStock] = useState<number>(0);
  const [location, setLocation] = useState('');
  const [weightKg, setWeightKg] = useState<number | ''>('');
  const [dimHeight, setDimHeight] = useState<number | ''>('');
  const [dimWidth, setDimWidth] = useState<number | ''>('');
  const [dimLength, setDimLength] = useState<number | ''>('');
  const [nicheMetadata, setNicheMetadata] = useState<Record<string, any>>({});

  // Estados de Imagem e Compressão Client-Side
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [compressionStats, setCompressionStats] = useState<{
    originalSizeKb: number;
    compressedSizeKb: number;
    ratio: number;
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Nicho detectado do usuário
  const userNiche = useMemo(() => {
    return user.nicheCategory || user.niche || user.profession || 'Geral / Revenda';
  }, [user]);

  // Filtra itens de venda/revenda respeitando o estado de arquivamento (Soft-Delete)
  const productList = useMemo(() => {
    return materials.filter(m => {
      const isProduct = m.itemType === 'produto_venda' || (m.category === 'variável' && (m.salePrice || 0) > 0);
      if (!isProduct) return false;
      if (showArchived) return m.isActive === false || Boolean(m.deletedAt);
      return m.isActive !== false && !m.deletedAt;
    });
  }, [materials, showArchived]);

  // Contagem de itens arquivados para acesso rápido do operador
  const archivedCount = useMemo(() => {
    return materials.filter(m => 
      (m.itemType === 'produto_venda' || (m.category === 'variável' && (m.salePrice || 0) > 0)) &&
      (m.isActive === false || Boolean(m.deletedAt))
    ).length;
  }, [materials]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return productList;
    const term = searchTerm.toLowerCase();
    return productList.filter(p => 
      p.name.toLowerCase().includes(term) ||
      (p.brand && p.brand.toLowerCase().includes(term)) ||
      (p.barcode && p.barcode.toLowerCase().includes(term)) ||
      (p.supplierName && p.supplierName.toLowerCase().includes(term)) ||
      (p.sku && p.sku.toLowerCase().includes(term)) ||
      (p.ncm && p.ncm.toLowerCase().includes(term))
    );
  }, [productList, searchTerm]);

  // Cálculos de métricas do estoque de revenda
  const metrics = useMemo(() => {
    const totalCount = productList.length;
    const totalCostValue = productList.reduce((acc, p) => acc + (p.pricePaid * (p.stockQuantity || 1)), 0);
    const totalSaleValue = productList.reduce((acc, p) => acc + ((p.salePrice || p.pricePaid * 1.4) * (p.stockQuantity || 1)), 0);
    const projectedProfit = totalSaleValue - totalCostValue;
    const lowStockCount = productList.filter(p => (p.minStock || 0) > 0 && (p.stockQuantity || 0) <= (p.minStock || 0)).length;
    return { totalCount, totalCostValue, totalSaleValue, projectedProfit, lowStockCount };
  }, [productList]);

  const handleOpenNewModal = () => {
    setEditingProduct(null);
    setName('');
    setBrand('');
    setCostPrice(0);
    setMarginPercent(40);
    setSalePrice(0);
    setUnit('un');
    setStockQuantity(1);
    setBarcode('');
    setSupplierName('');
    setStockControlMode('unit_sale');
    setPackQuantity(1);
    setImageUrl('');
    setCompressionStats(null);
    
    // Master fields reset
    setSku('');
    setNcm('');
    setCest('');
    setOriginCode(0);
    setMinStock(0);
    setLocation('');
    setWeightKg('');
    setDimHeight('');
    setDimWidth('');
    setDimLength('');
    setNicheMetadata({});
    setShowAdvancedDetails(false);
    setActiveAdvancedTab('master');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (prod: Material) => {
    setEditingProduct(prod);
    setName(prod.name);
    setBrand(prod.brand || '');
    setCostPrice(prod.pricePaid);
    setMarginPercent(prod.marginPercent || 40);
    setSalePrice(prod.salePrice || Math.round(prod.pricePaid * 1.4));
    setUnit(prod.unit || 'un');
    setStockQuantity(prod.stockQuantity ?? 1);
    setBarcode(prod.barcode || '');
    setSupplierName(prod.supplierName || '');
    setStockControlMode(prod.stockControlMode || 'unit_sale');
    setPackQuantity(prod.packQuantity || 1);
    setImageUrl(prod.imageUrl || '');
    setCompressionStats(null);

    // Master fields populate
    setSku(prod.sku || '');
    setNcm(prod.ncm || '');
    setCest(prod.cest || '');
    setOriginCode(prod.originCode ?? 0);
    setMinStock(prod.minStock || 0);
    setLocation(prod.location || '');
    setWeightKg(prod.weightKg ?? '');
    setDimHeight(prod.dimensionsCm?.height ?? '');
    setDimWidth(prod.dimensionsCm?.width ?? '');
    setDimLength(prod.dimensionsCm?.length ?? '');
    setNicheMetadata(prod.nicheMetadata || {});

    // Se já tiver campos avançados preenchidos, podemos exibir ou manter colapsado
    const hasAdvancedFilled = !!(prod.sku || prod.ncm || (prod.minStock && prod.minStock > 0) || prod.location || prod.weightKg);
    setShowAdvancedDetails(hasAdvancedFilled);
    setActiveAdvancedTab('master');
    setIsModalOpen(true);
  };

  // Gerador automático de SKU rápido
  const handleGenerateSku = () => {
    const prefix = name ? name.trim().slice(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, 'PRD') : 'PRD';
    const random = Math.floor(100 + Math.random() * 900);
    setSku(`${prefix}-${random}`);
  };

  // Handler de seleção de imagem com compressão instantânea client-side
  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    try {
      setIsCompressingImage(true);
      const compressed = await compressProductImage(file, 500, 0.8);
      setCompressionStats({
        originalSizeKb: compressed.originalSizeKb,
        compressedSizeKb: compressed.compressedSizeKb,
        ratio: compressed.compressionRatioPercent
      });

      setImageUrl(compressed.base64);

      setIsUploadingImage(true);
      const publicUrl = await uploadProductPhoto(user.id, compressed.blob, editingProduct?.id);
      setImageUrl(publicUrl);
    } catch (err: any) {
      console.error('Erro ao processar imagem:', err);
      alert(`Não foi possível enviar a imagem: ${err.message || 'Erro no upload'}`);
    } finally {
      setIsCompressingImage(false);
      setIsUploadingImage(false);
    }
  };

  const handleRemovePhoto = () => {
    setImageUrl('');
    setCompressionStats(null);
  };

  // Recalcula o Preço de Venda quando o Custo ou a Margem mudam
  const handleCostChange = (cost: number) => {
    setCostPrice(cost);
    const marginDec = (marginPercent || 40) / 100;
    const calculated = marginDec < 1 ? cost / (1 - marginDec) : cost * 1.4;
    setSalePrice(Math.round(calculated * 100) / 100);
  };

  const handleMarginChange = (margin: number) => {
    setMarginPercent(margin);
    const marginDec = margin / 100;
    const calculated = marginDec < 1 ? costPrice / (1 - marginDec) : costPrice * 1.4;
    setSalePrice(Math.round(calculated * 100) / 100);
  };

  const handleSalePriceDirectChange = (price: number) => {
    setSalePrice(price);
    if (costPrice > 0 && price > costPrice) {
      const calculatedMargin = Math.round(((price - costPrice) / price) * 100);
      setMarginPercent(calculatedMargin);
    }
  };

  const handleSaveProduct = () => {
    if (!name.trim()) {
      alert('Informe o nome do produto.');
      return;
    }
    if (costPrice <= 0) {
      alert('Informe o preço de custo do produto.');
      return;
    }

    // 1. Validação estrita de unicidade do código de barras
    const cleanBarcode = barcode.trim();
    if (cleanBarcode) {
      const duplicate = materials.find(m => 
        m.id !== editingProduct?.id &&
        m.isActive !== false &&
        !m.deletedAt &&
        m.barcode &&
        m.barcode.trim().toLowerCase() === cleanBarcode.toLowerCase()
      );
      if (duplicate) {
        alert(
          `O código de barras "${cleanBarcode}" já está em uso pelo produto "${duplicate.name}".\n\n` +
          `Para evitar itens duplicados no PDV e estoque, informe outro código ou edite o produto já existente.`
        );
        return;
      }
    }

    const effectiveSalePrice = salePrice > 0 ? salePrice : Math.round(costPrice * 1.4 * 100) / 100;
    const updatedProduct: Material = {
      id: editingProduct?.id || `prod_${Date.now()}`,
      userId: user.id,
      name: name.trim(),
      brand: brand.trim() || undefined,
      pricePaid: costPrice,
      yieldAmount: 1,
      unit: unit.trim() || 'un',
      category: 'variável',
      itemType: 'produto_venda',
      salePrice: effectiveSalePrice,
      marginPercent: marginPercent,
      stockQuantity: stockQuantity,
      barcode: cleanBarcode || undefined,
      supplierName: supplierName.trim() || undefined,
      stockControlMode: stockControlMode,
      packQuantity: packQuantity > 1 ? packQuantity : undefined,
      imageUrl: imageUrl.trim() || undefined,
      isActive: editingProduct ? (editingProduct.isActive !== false) : true,
      deletedAt: editingProduct?.deletedAt,
      // Campos Master e Nichos
      sku: sku.trim() || undefined,
      ncm: ncm.trim() || undefined,
      cest: cest.trim() || undefined,
      originCode: originCode ?? 0,
      minStock: minStock > 0 ? minStock : undefined,
      location: location.trim() || undefined,
      weightKg: weightKg !== '' && Number(weightKg) > 0 ? Number(weightKg) : undefined,
      dimensionsCm: (dimHeight !== '' || dimWidth !== '' || dimLength !== '') ? {
        height: dimHeight !== '' ? Number(dimHeight) : undefined,
        width: dimWidth !== '' ? Number(dimWidth) : undefined,
        length: dimLength !== '' ? Number(dimLength) : undefined,
      } : undefined,
      nicheCategory: userNiche,
      nicheMetadata: Object.keys(nicheMetadata).length > 0 ? nicheMetadata : undefined
    };

    let updatedList: Material[];
    if (editingProduct) {
      updatedList = materials.map(m => m.id === editingProduct.id ? updatedProduct : m);
    } else {
      updatedList = [...materials, updatedProduct];
    }

    onSaveMaterials(updatedList);
    setIsModalOpen(false);
    setEditingProduct(null);
    setScannerBanner(`Produto "${updatedProduct.name}" salvo com sucesso!`);
    setTimeout(() => setScannerBanner(null), 3500);
  };

  const handleRequestDelete = (prod: Material) => {
    setProductToDelete(prod);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = (mode: 'soft' | 'hard') => {
    if (!productToDelete) return;
    const targetId = productToDelete.id;

    let updatedList: Material[];
    if (mode === 'soft') {
      // Exclusão Lógica (Soft-delete): resguarda dados fiscais, DRE e vendas anteriores
      updatedList = materials.map(m => m.id === targetId ? {
        ...m,
        isActive: false,
        deletedAt: new Date().toISOString()
      } : m);
      setScannerBanner(`Produto "${productToDelete.name}" foi desativado do catálogo ativo.`);
    } else {
      // Exclusão Física (Hard-delete): remoção definitiva para itens cadastrados incorretamente/duplicados
      updatedList = materials.filter(m => m.id !== targetId);
      setScannerBanner(`Produto "${productToDelete.name}" foi excluído permanentemente.`);
    }

    onSaveMaterials(updatedList);
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
    if (isModalOpen && editingProduct?.id === targetId) {
      setIsModalOpen(false);
      setEditingProduct(null);
    }
    setTimeout(() => setScannerBanner(null), 4000);
  };

  const handleRestoreProduct = (prod: Material) => {
    const updatedList = materials.map(m => m.id === prod.id ? {
      ...m,
      isActive: true,
      deletedAt: undefined
    } : m);
    onSaveMaterials(updatedList);
    setScannerBanner(`Produto "${prod.name}" foi restaurado com sucesso!`);
    setTimeout(() => setScannerBanner(null), 4000);
  };

  const handleQuickAdjustStock = (prod: Material, delta: number) => {
    const current = Number(prod.stockQuantity ?? 0);
    const next = Math.max(0, Number((current + delta).toFixed(3)));
    const updated = materials.map(m => m.id === prod.id ? { ...m, stockQuantity: next } : m);
    onSaveMaterials(updated);
    setScannerBanner(
      delta < 0
        ? `Baixa registrada em "${prod.name}": ${current} -> ${next} ${prod.unit || 'un'}`
        : `Reposição registrada em "${prod.name}": ${current} -> ${next} ${prod.unit || 'un'}`
    );
    setTimeout(() => setScannerBanner(null), 4000);
  };

  // Leitor de Código de Barras / QR Code Externo (Hardware USB / Bluetooth)
  useHardwareScanner({
    enabled: true,
    onScan: (parsedData) => {
      const cleanCode = (parsedData.gtin || parsedData.raw || '').trim();
      if (!cleanCode) return;

      if (isModalOpen) {
        setBarcode(cleanCode);
        setScannerBanner(`Código de barras ${cleanCode} inserido no formulário.`);
        setTimeout(() => setScannerBanner(null), 3000);
        return;
      }

      // Procura primeiro nos produtos ativos
      const found = productList.find(p => p.barcode?.trim().toLowerCase() === cleanCode.toLowerCase());
      if (found) {
        // Opção A Aprimorada: Não deduz estoque silenciosamente! Abre diretamente para conferência
        handleOpenEditModal(found);
        setScannerBanner(`Código ${cleanCode} já associado a "${found.name}". Item aberto para conferência.`);
        setTimeout(() => setScannerBanner(null), 4000);
      } else {
        // Se for código inédito, abre o modal de novo produto já com o código preenchido
        handleOpenNewModal();
        setBarcode(cleanCode);
        setScannerBanner(`Novo item iniciado com o código ${cleanCode}. Preencha e salve.`);
        setTimeout(() => setScannerBanner(null), 4000);
      }
    },
    maxKeystrokeIntervalMs: 45
  });

  return (
    <div className="h-full min-h-0 overflow-y-auto w-full pb-32 px-4 pt-6 space-y-6">
      {/* Header com Resumo e Ação de Cadastro */}
      <div className="bg-white border-2 border-emerald-100 rounded-3xl p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 text-brand p-3 rounded-2xl">
              <ShoppingBag className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                  Estoque & Revenda Master
                </span>
                <span className="text-[10px] font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full border border-zinc-200">
                  Nicho: {userNiche}
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-black text-zinc-900">
                Produtos & Mercadorias de Venda
              </h2>
              <p className="text-xs text-zinc-500">
                Cadastre mercadorias avulsas com precificação inteligente, códigos e suporte a nichos.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canAccessStock && onNavigateToStock && (
              <button
                type="button"
                onClick={onNavigateToStock}
                className="px-3.5 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 border border-zinc-200"
                title="Ir para o controle de estoque, lotes e movimentações"
              >
                <Boxes className="h-4 w-4 text-zinc-600" />
                <span className="hidden sm:inline">Estoque & Lotes</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleOpenNewModal}
              className="px-4 py-3 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-sm shadow-brand/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
            >
              <Plus className="h-4 w-4" />
              <span>Novo Produto</span>
            </button>
          </div>
        </div>

        {/* 4 Cards de Métricas Rápidas + Alerta de Estoque Mínimo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-zinc-100">
          <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-3.5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider">Produtos Ativos</span>
              {metrics.lowStockCount > 0 && (
                <span className="text-[9px] font-black uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-md flex items-center gap-0.5" title="Produtos abaixo do estoque mínimo">
                  <AlertTriangle className="w-2.5 h-2.5" />
                  {metrics.lowStockCount} repor
                </span>
              )}
            </div>
            <div className="text-lg font-black text-zinc-900">{metrics.totalCount} itens</div>
          </div>
          <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-3.5 space-y-1">
            <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider">Custo Total em Estoque</span>
            <div className="text-lg font-black text-zinc-800">{formatCurrencyBRL(metrics.totalCostValue)}</div>
          </div>
          <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-3.5 space-y-1">
            <span className="text-[10px] font-black uppercase text-emerald-700 tracking-wider">Potencial de Venda</span>
            <div className="text-lg font-black text-emerald-700">{formatCurrencyBRL(metrics.totalSaleValue)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 space-y-1">
            <span className="text-[10px] font-black uppercase text-brand tracking-wider">Lucro Projetado</span>
            <div className="text-lg font-black text-brand">+{formatCurrencyBRL(metrics.projectedProfit)}</div>
          </div>
        </div>
      </div>

      {/* Barra de Pesquisa Abrangente + Aviso de Leitor Externo */}
      {scannerBanner && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl px-4 py-3 text-xs font-bold flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Barcode className="w-4 h-4 text-emerald-700" />
            <span>{scannerBanner}</span>
          </div>
          <button type="button" onClick={() => setScannerBanner(null)} className="text-emerald-700 hover:text-emerald-950 font-black">✕</button>
        </div>
      )}

      {/* Abas de Navegação: Catálogo Ativo vs Arquivados (Soft-Delete) */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowArchived(false)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              !showArchived
                ? 'bg-zinc-900 text-white shadow-xs'
                : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
            }`}
          >
            Produtos Ativos ({materials.filter(m => (m.itemType === 'produto_venda' || (m.category === 'variável' && (m.salePrice || 0) > 0)) && m.isActive !== false && !m.deletedAt).length})
          </button>
          
          <button
            type="button"
            onClick={() => setShowArchived(true)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              showArchived
                ? 'bg-amber-600 text-white shadow-xs'
                : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
            }`}
          >
            <Archive className="w-3.5 h-3.5" />
            <span>Itens Arquivados ({archivedCount})</span>
          </button>
        </div>

        {showArchived && (
          <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
            Modo Arquivo: produtos ocultos do PDV
          </span>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Buscar ou bipar código de barras de água, picolé, fornecedor, marca ou SKU..."
          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-4 py-3 text-xs font-bold text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 rounded-full"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Lista / Grid de Produtos */}
      {filteredProducts.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-zinc-200 rounded-3xl p-10 text-center space-y-4">
          <div className="w-14 h-14 bg-zinc-100 text-zinc-400 rounded-2xl flex items-center justify-center mx-auto">
            <ShoppingBag className="h-7 w-7" />
          </div>
          <div className="space-y-1 max-w-sm mx-auto">
            <h3 className="text-sm font-black text-zinc-900">
              {searchTerm ? 'Nenhum produto encontrado' : 'Nenhum produto para revenda cadastrado'}
            </h3>
            <p className="text-xs text-zinc-500">
              {searchTerm 
                ? 'Tente outro termo na busca por nome, SKU, código de barras ou marca.' 
                : 'Cadastre seus produtos com precificação instantânea e controle simples.'}
            </p>
          </div>
          {!searchTerm && (
            <button
              type="button"
              onClick={handleOpenNewModal}
              className="px-4 py-2.5 bg-brand text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm cursor-pointer"
            >
              + Cadastrar Primeiro Produto
            </button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3.5">
          {filteredProducts.map(product => {
            const cost = product.pricePaid || 0;
            const sale = product.salePrice || Math.round(cost * 1.4);
            const profit = sale - cost;
            const margin = product.marginPercent || (sale > 0 ? Math.round((profit / sale) * 100) : 0);
            const isLowStock = (product.minStock || 0) > 0 && (product.stockQuantity || 0) <= (product.minStock || 0);

            return (
              <div 
                key={product.id}
                className={`bg-white border ${isLowStock ? 'border-amber-300' : 'border-zinc-200/90'} hover:border-emerald-500 rounded-3xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex flex-col justify-between gap-4 group`}
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {/* Miniatura da Foto com Compressão */}
                      <div className="w-14 h-14 rounded-2xl bg-zinc-100 border border-zinc-200 overflow-hidden shrink-0 flex items-center justify-center relative group-hover:border-emerald-300 transition-colors">
                        {product.imageUrl ? (
                          <img 
                            src={product.imageUrl} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="text-zinc-400">
                            <ShoppingBag className="w-6 h-6" />
                          </div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[9px] font-black text-emerald-700 uppercase tracking-wider bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                            {product.unit || 'UNIDADE'}
                          </span>
                          {product.brand && (
                            <span className="text-[10px] text-zinc-500 font-bold uppercase">
                              • {product.brand}
                            </span>
                          )}
                          {product.sku && (
                            <span className="text-[9px] font-mono font-bold text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded border border-zinc-200">
                              SKU: {product.sku}
                            </span>
                          )}
                        </div>
                        <h3 className="text-sm font-black text-zinc-900 group-hover:text-emerald-700 transition-colors leading-snug">
                          {product.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                          {product.supplierName && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-md">
                              <Truck className="w-3 h-3 text-zinc-500" />
                              {product.supplierName}
                            </span>
                          )}
                          {product.barcode && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-md">
                              <Barcode className="w-3 h-3 text-zinc-500" />
                              {product.barcode}
                            </span>
                          )}
                        </div>
                        {product.location && (
                          <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-medium">
                            <MapPin className="w-3 h-3 text-zinc-400" />
                            <span>{product.location}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {showArchived ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleRestoreProduct(product)}
                            className="px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors cursor-pointer flex items-center gap-1"
                            title="Restaurar item para o catálogo ativo"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            <span>Restaurar</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRequestDelete(product)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer"
                            title="Excluir Definitivamente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(product)}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-xl transition-colors cursor-pointer"
                            title="Editar Produto"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRequestDelete(product)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer"
                            title="Excluir ou Desativar Produto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bloco de Preços & Lucro */}
                <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-3 grid grid-cols-3 gap-2 text-center">
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-black uppercase text-zinc-400 block">Custo</span>
                    <div className="text-xs font-black text-zinc-700">{formatCurrencyBRL(cost)}</div>
                  </div>
                  <div className="space-y-0.5 border-x border-zinc-200">
                    <span className="text-[9px] font-black uppercase text-emerald-600 block">Margem</span>
                    <div className="text-xs font-black text-emerald-700">{margin}%</div>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-[9px] font-black uppercase text-brand block">Venda</span>
                    <div className="text-xs font-black text-brand">{formatCurrencyBRL(sale)}</div>
                  </div>
                </div>

                {/* Rodapé com Estoque, Baixa Rápida (Água/Picolé/Pote) e Lucro Unitário */}
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500 pt-2 border-t border-zinc-100">
                  <div className="flex items-center gap-2 font-bold">
                    <div className="flex items-center gap-1.5">
                      <Boxes className="h-3.5 w-3.5 text-zinc-400" />
                      <span>Estoque: <strong className="text-zinc-800">{product.stockQuantity ?? 0} {product.unit}</strong></span>
                    </div>
                    {isLowStock && (
                      <span className="text-[9px] font-black uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Repor (Mín: {product.minStock})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleQuickAdjustStock(product, -1)}
                      className="px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-500 text-amber-800 hover:text-white border border-amber-200 text-[10px] font-black uppercase transition-colors cursor-pointer"
                      title="Dar baixa de 1 unidade / pote no estoque"
                    >
                      -1 Baixa
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickAdjustStock(product, 1)}
                      className="px-2 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-600 text-emerald-800 hover:text-white border border-emerald-200 text-[10px] font-black uppercase transition-colors cursor-pointer"
                      title="Repor +1 unidade no estoque"
                    >
                      +1
                    </button>
                    {product.packQuantity && product.packQuantity > 1 && (
                      <button
                        type="button"
                        onClick={() => handleQuickAdjustStock(product, product.packQuantity!)}
                        className="px-2 py-1 rounded-lg bg-blue-50 hover:bg-blue-600 text-blue-800 hover:text-white border border-blue-200 text-[10px] font-black uppercase transition-colors cursor-pointer"
                        title={`Repor 1 fardo/caixa (+${product.packQuantity} ${product.unit})`}
                      >
                        +{product.packQuantity} Cx
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Criação / Edição de Produto com Progressive Disclosure */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-120 flex items-center justify-center p-4 bg-zinc-950/70 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-zinc-200 rounded-3xl p-5 sm:p-6 w-full max-w-xl shadow-2xl space-y-4 max-h-[92vh] flex flex-col"
            >
              {/* Header do Modal */}
              <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 text-brand flex items-center justify-center">
                    <ShoppingBag className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-zinc-900">
                      {editingProduct ? 'Editar Produto de Revenda' : 'Novo Produto de Revenda'}
                    </h3>
                    <p className="text-[11px] text-zinc-500">Defina os valores básicos ou expanda para a tabela master.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-600 rounded-full cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Corpo Rolável do Formulário */}
              <div className="space-y-3.5 text-xs overflow-y-auto pr-1 flex-1">
                {/* 1. Foto do Produto com Compressão no Navegador */}
                <div className="p-3 bg-zinc-50 border border-zinc-200/90 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase text-zinc-500 tracking-wider flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5 text-zinc-600" />
                      <span>Foto do Produto (Compactação WebP)</span>
                    </span>
                    {compressionStats && (
                      <span className="text-[10px] font-black text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        <span>-{compressionStats.ratio}% ({compressionStats.compressedSizeKb} KB)</span>
                      </span>
                    )}
                  </div>

                  <input 
                    type="file" 
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageSelected}
                    className="hidden"
                  />

                  {imageUrl ? (
                    <div className="flex items-center gap-3 bg-white p-2.5 rounded-xl border border-zinc-200">
                      <div className="w-16 h-16 rounded-xl bg-zinc-100 border border-zinc-200 overflow-hidden shrink-0 relative">
                        <img 
                          src={imageUrl} 
                          alt="Prévia do produto" 
                          className="w-full h-full object-cover"
                        />
                        {(isCompressingImage || isUploadingImage) && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 text-white animate-spin" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-zinc-800">Foto otimizada pronta</span>
                          {isUploadingImage && (
                            <span className="text-[10px] font-bold text-blue-600 animate-pulse">Sincronizando...</span>
                          )}
                        </div>
                        {compressionStats ? (
                          <p className="text-[11px] text-zinc-500">
                            De <strong>{compressionStats.originalSizeKb} KB</strong> para apenas <strong>{compressionStats.compressedSizeKb} KB</strong>.
                          </p>
                        ) : (
                          <p className="text-[11px] text-zinc-400">Imagem leve pronta para o catálogo e orçamentos.</p>
                        )}
                        <div className="flex items-center gap-2 pt-0.5">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-[11px] font-bold text-brand hover:underline cursor-pointer"
                          >
                            Trocar Foto
                          </button>
                          <span className="text-zinc-300">•</span>
                          <button
                            type="button"
                            onClick={handleRemovePhoto}
                            className="text-[11px] font-bold text-red-600 hover:underline cursor-pointer"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-zinc-300 hover:border-emerald-500 bg-white hover:bg-emerald-50/30 rounded-xl p-3.5 text-center cursor-pointer transition-all space-y-1"
                    >
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 mx-auto flex items-center justify-center">
                        {isCompressingImage ? (
                          <Loader2 className="w-4 h-4 animate-spin text-brand" />
                        ) : (
                          <ImageIcon className="w-4 h-4" />
                        )}
                      </div>
                      <div className="text-xs font-bold text-zinc-800">
                        {isCompressingImage ? 'Comprimindo foto no navegador...' : 'Adicionar foto do produto'}
                      </div>
                      <p className="text-[10px] text-zinc-400 max-w-xs mx-auto">
                        Tire uma foto ou escolha da galeria (~20KB sem perda visual).
                      </p>
                    </div>
                  )}
                </div>

                {/* 2. Nome do Produto */}
                <div>
                  <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                    Nome do Produto / Mercadoria *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Ex: Pomada Modeladora Matte 150g, Disjuntor DIN 20A..."
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                {/* 3. Marca e Unidade com Pills Rápidas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                      Marca / Fabricante
                    </label>
                    <input
                      type="text"
                      value={brand}
                      onChange={e => setBrand(e.target.value)}
                      placeholder="Ex: Schneider, Keune, Tigre, Bosch"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                      Unidade de Venda
                    </label>
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        value={unit}
                        onChange={e => setUnit(e.target.value)}
                        placeholder="un, cx, kg, m, rolo"
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand/20"
                      />
                      {/* Pills de atalho rápido */}
                      <div className="flex flex-wrap items-center gap-1">
                        {COMMON_UNITS.map(u => (
                          <button
                            key={u}
                            type="button"
                            onClick={() => setUnit(u)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${
                              unit.toLowerCase() === u.toLowerCase()
                                ? 'bg-brand text-white'
                                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                          >
                            {u}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Bloco de Precificação com Calculadora de Margem Integrada */}
                <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-brand uppercase tracking-widest block">
                      Calculadora de Preço de Venda
                    </span>
                    <span className="text-[10px] text-emerald-700 font-bold bg-white px-2 py-0.5 rounded-full border border-emerald-200">
                      Markup Divisor
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2.5">
                    <div>
                      <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Custo (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={costPrice || ''}
                        onChange={e => handleCostChange(parseFloat(e.target.value) || 0)}
                        placeholder="0,00"
                        className="w-full bg-white border border-emerald-200 rounded-xl px-2.5 py-2 text-xs font-black text-zinc-900 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black uppercase text-emerald-700 block mb-1">Margem (%)</label>
                      <input
                        type="number"
                        value={marginPercent || ''}
                        onChange={e => handleMarginChange(parseFloat(e.target.value) || 0)}
                        placeholder="40"
                        className="w-full bg-white border border-emerald-200 rounded-xl px-2.5 py-2 text-xs font-black text-emerald-700 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-black uppercase text-brand block mb-1">Venda Final (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={salePrice || ''}
                        onChange={e => handleSalePriceDirectChange(parseFloat(e.target.value) || 0)}
                        placeholder="0,00"
                        className="w-full bg-white border-2 border-brand rounded-xl px-2.5 py-2 text-xs font-black text-brand focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="text-[11px] font-bold text-emerald-800 flex items-center justify-between pt-1 border-t border-emerald-200">
                    <span>Lucro Bruto por Unidade:</span>
                    <strong className="text-sm text-brand">+{formatCurrencyBRL(Math.max(0, (salePrice || 0) - (costPrice || 0)))}</strong>
                  </div>
                </div>

                {/* 5. Fornecedor, Código de Barras (Leitora Externa) e Estoque */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider flex items-center gap-1 mb-1">
                      <Truck className="w-3.5 h-3.5 text-zinc-500" />
                      Nome do Fornecedor
                    </label>
                    <input
                      type="text"
                      value={supplierName}
                      onChange={e => setSupplierName(e.target.value)}
                      placeholder="Ex: Crystal, Kibon, Distribuidora..."
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider flex items-center gap-1 mb-1">
                      <Barcode className="w-3.5 h-3.5 text-zinc-500" />
                      Código de Barras / EAN (Leitora Externa)
                    </label>
                    <input
                      type="text"
                      data-barcode-input="true"
                      value={barcode}
                      onChange={e => setBarcode(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Bipe com a leitora ou digite o EAN"
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs font-mono font-bold text-zinc-900 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-zinc-50 border border-zinc-200 rounded-2xl p-3">
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                      Quantidade em Estoque
                    </label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        value={stockQuantity}
                        onChange={e => setStockQuantity(Math.max(0, parseInt(e.target.value, 10) || 0))}
                        placeholder="1"
                        className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setStockQuantity(q => Math.max(0, q - 1))}
                        className="px-2.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase shrink-0"
                        title="Dar baixa de 1 unidade"
                      >
                        -1
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                      Modo de Baixa
                    </label>
                    <select
                      value={stockControlMode}
                      onChange={e => setStockControlMode(e.target.value as StockControlMode)}
                      className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                    >
                      <option value="unit_sale">Água / Picolé / Unitário</option>
                      <option value="pot_open">Pote / Caixa (Ao Abrir)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                      Unid. por Fardo/Caixa
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="1"
                        value={packQuantity}
                        onChange={e => setPackQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        className="w-16 bg-white border border-zinc-200 rounded-xl px-2.5 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                      />
                      {[12, 24].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setPackQuantity(n)}
                          className={`px-2 py-1.5 rounded-lg text-[10px] font-black border ${
                            packQuantity === n ? 'bg-brand text-white border-brand' : 'bg-white text-zinc-600 border-zinc-200'
                          }`}
                        >
                          {n}x
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ============================================================================== */}
                {/* PROGRESSIVE DISCLOSURE: O BOTÃO "+ MAIS DETALHES (TABELA MASTER)" */}
                {/* ============================================================================== */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedDetails(!showAdvancedDetails)}
                    className="w-full py-2.5 px-3 bg-zinc-100 hover:bg-emerald-50 text-zinc-700 hover:text-emerald-800 border border-zinc-200 hover:border-emerald-300 rounded-2xl text-xs font-black flex items-center justify-between transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="p-1 rounded-lg bg-white border border-zinc-200 shadow-2xs">
                        {showAdvancedDetails ? <ChevronUp className="w-3.5 h-3.5 text-emerald-700" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                      </span>
                      <span>
                        {showAdvancedDetails ? 'Ocultar Detalhes Opcionais' : '+ Mais Detalhes (SKU, Fiscal NCM, Estoque Mínimo, Nicho)'}
                      </span>
                    </div>
                    <span className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-full font-bold">
                      Tabela Master
                    </span>
                  </button>
                </div>

                {/* PAINEL EXPANSÍVEL DA TABELA MASTER */}
                <AnimatePresence>
                  {showAdvancedDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden space-y-3 pt-2"
                    >
                      {/* Abas Subordinadas dos Detalhes Avançados */}
                      <div className="flex items-center gap-1.5 p-1 bg-zinc-100 rounded-xl border border-zinc-200 text-[11px] font-bold">
                        <button
                          type="button"
                          onClick={() => setActiveAdvancedTab('master')}
                          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                            activeAdvancedTab === 'master' ? 'bg-white text-zinc-900 shadow-xs' : 'text-zinc-500 hover:text-zinc-800'
                          }`}
                        >
                          <Tag className="w-3 h-3" />
                          <span>Códigos & Fiscal</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveAdvancedTab('stock')}
                          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                            activeAdvancedTab === 'stock' ? 'bg-white text-zinc-900 shadow-xs' : 'text-zinc-500 hover:text-zinc-800'
                          }`}
                        >
                          <Boxes className="w-3 h-3" />
                          <span>Estoque & Logística</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveAdvancedTab('niche')}
                          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                            activeAdvancedTab === 'niche' ? 'bg-white text-zinc-900 shadow-xs' : 'text-zinc-500 hover:text-zinc-800'
                          }`}
                        >
                          <Layers className="w-3 h-3" />
                          <span>Nicho ({userNiche.split(' ')[0]})</span>
                        </button>
                      </div>

                      {/* SUB-ABA 1: CÓDIGOS & FISCAL */}
                      {activeAdvancedTab === 'master' && (
                        <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider">
                                  SKU / Código Interno
                                </label>
                                <button
                                  type="button"
                                  onClick={handleGenerateSku}
                                  className="text-[10px] font-bold text-emerald-700 hover:underline flex items-center gap-1 cursor-pointer"
                                  title="Gerar código SKU automático"
                                >
                                  <Zap className="w-3 h-3" />
                                  <span>Gerar SKU</span>
                                </button>
                              </div>
                              <input
                                type="text"
                                value={sku}
                                onChange={e => setSku(e.target.value)}
                                placeholder="Ex: POM-001, CAB-025"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Origem da Mercadoria
                              </label>
                              <select
                                value={originCode}
                                onChange={e => setOriginCode(parseInt(e.target.value, 10) || 0)}
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              >
                                <option value={0}>0 - Nacional</option>
                                <option value={1}>1 - Estrangeira (Importação direta)</option>
                                <option value={2}>2 - Estrangeira (Mercado interno)</option>
                              </select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Código NCM (Fiscal - 8 dígitos)
                              </label>
                              <input
                                type="text"
                                value={ncm}
                                onChange={e => setNcm(e.target.value)}
                                placeholder="Ex: 3305.90.00"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Código CEST (Opcional)
                              </label>
                              <input
                                type="text"
                                value={cest}
                                onChange={e => setCest(e.target.value)}
                                placeholder="Ex: 20.015.00"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                          </div>

                          {/* Sugestões de NCMs Rápidos */}
                          <div className="pt-1">
                            <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                              Sugestões Frequentes de NCM:
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {NCM_SUGGESTIONS.slice(0, 4).map(s => (
                                <button
                                  key={s.ncm}
                                  type="button"
                                  onClick={() => setNcm(s.ncm)}
                                  className="text-[10px] bg-white hover:bg-emerald-50 text-zinc-700 hover:text-emerald-800 border border-zinc-200 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                                  title={`${s.label} (${s.niche})`}
                                >
                                  <strong>{s.ncm}</strong> - {s.label.split(' ')[0]}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* SUB-ABA 2: ESTOQUE AVANÇADO & LOGÍSTICA */}
                      {activeAdvancedTab === 'stock' && (
                        <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Estoque Mínimo (Alerta de Reposição)
                              </label>
                              <input
                                type="number"
                                value={minStock || ''}
                                onChange={e => setMinStock(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                placeholder="Ex: 3 unidades"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                              <span className="text-[10px] text-zinc-400 mt-0.5 block">
                                Emite aviso quando atingir essa quantidade.
                              </span>
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Localização Física no Espaço
                              </label>
                              <input
                                type="text"
                                value={location}
                                onChange={e => setLocation(e.target.value)}
                                placeholder="Ex: Prateleira B2, Gaveta 3, Balcão"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                            <div>
                              <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Peso (kg)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={weightKg}
                                onChange={e => setWeightKg(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                placeholder="0,250"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Alt (cm)</label>
                              <input
                                type="number"
                                value={dimHeight}
                                onChange={e => setDimHeight(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                placeholder="10"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Larg (cm)</label>
                              <input
                                type="number"
                                value={dimWidth}
                                onChange={e => setDimWidth(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                placeholder="15"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-black uppercase text-zinc-500 block mb-1">Comp (cm)</label>
                              <input
                                type="number"
                                value={dimLength}
                                onChange={e => setDimLength(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                placeholder="20"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* SUB-ABA 3: METADADOS ESPECÍFICOS DO NICHO */}
                      {activeAdvancedTab === 'niche' && (
                        <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                          <div className="flex items-center justify-between border-b border-zinc-200 pb-2">
                            <span className="text-[10px] font-black uppercase text-emerald-800 tracking-wider">
                              Campos Customizados ({userNiche})
                            </span>
                            <span className="text-[10px] text-zinc-400">Salvo na Tabela Master</span>
                          </div>

                          {/* Campos dinâmicos contextuais */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Referência do Fabricante / Código OEM
                              </label>
                              <input
                                type="text"
                                value={nicheMetadata.oem_code || ''}
                                onChange={e => setNicheMetadata({ ...nicheMetadata, oem_code: e.target.value })}
                                placeholder="Ex: Ref 4022-X, Código da Peça"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                                Conteúdo / Linha / Especificação
                              </label>
                              <input
                                type="text"
                                value={nicheMetadata.line_spec || ''}
                                onChange={e => setNicheMetadata({ ...nicheMetadata, line_spec: e.target.value })}
                                placeholder="Ex: 150ml, Uso Profissional, 220V, Tam G"
                                className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-[10px] font-black uppercase text-zinc-500 tracking-wider block mb-1">
                              Observações ou Detalhes Técnicos para o Orçamento
                            </label>
                            <input
                              type="text"
                              value={nicheMetadata.technical_notes || ''}
                              onChange={e => setNicheMetadata({ ...nicheMetadata, technical_notes: e.target.value })}
                              placeholder="Informações adicionais que auxiliam na venda ou no PDF"
                              className="w-full bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none"
                            />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Botões do Rodapé */}
              <div className="flex items-center gap-2 pt-3 border-t border-zinc-100 shrink-0">
                {editingProduct && (
                  <button
                    type="button"
                    onClick={() => handleRequestDelete(editingProduct)}
                    className="py-3 px-3.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                    title="Excluir ou desativar este produto"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Excluir</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveProduct}
                  className="flex-1 py-3 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md shadow-brand/20 transition-all cursor-pointer active:scale-95"
                >
                  {editingProduct ? 'Salvar Alterações' : 'Cadastrar Produto'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO / DESATIVAÇÃO (MATERIAL DESIGN 3) */}
      <AnimatePresence>
        {isDeleteModalOpen && productToDelete && (
          <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl max-w-md w-full border border-zinc-200 shadow-2xl p-6 space-y-5 animate-in"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100">
                  <Trash2 className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-base font-black text-zinc-900 leading-snug">
                    Excluir ou Desativar Produto
                  </h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    Item: <strong>{productToDelete.name}</strong>
                    {productToDelete.barcode ? ` (Código: ${productToDelete.barcode})` : ''}
                  </p>
                </div>
              </div>

              <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 text-xs text-zinc-600 space-y-2">
                <div className="flex justify-between items-center text-zinc-700">
                  <span>Saldo Atual:</span>
                  <strong className="font-bold text-zinc-900">{productToDelete.stockQuantity ?? 0} {productToDelete.unit || 'un'}</strong>
                </div>
                <div className="flex justify-between items-center text-zinc-700">
                  <span>Preço de Venda:</span>
                  <strong className="font-bold text-brand">{formatCurrencyBRL(productToDelete.salePrice || 0)}</strong>
                </div>
                <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-200 leading-relaxed">
                  <strong>Recomendação:</strong> Se este item já realizou vendas ou movimentações no PDV, escolha <strong>Desativar</strong> para resguardar o histórico fiscal e os relatórios. Se foi cadastrado por engano ou duplicado, escolha <strong>Excluir Definitivamente</strong>.
                </p>
              </div>

              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={() => handleConfirmDelete('soft')}
                  className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-xs"
                >
                  <Archive className="w-4 h-4" />
                  <span>Desativar Produto (Recomendado)</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleConfirmDelete('hard')}
                  className="w-full py-2.5 px-4 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Excluir Definitivamente</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setProductToDelete(null);
                  }}
                  className="w-full py-2 text-zinc-500 hover:text-zinc-800 font-bold text-xs transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
